import { z } from 'zod';
/** Model ecosystems (doc 48): which LoRAs, VAEs and text encoders work together. Order = display order. */
export const FAMILY_IDS = [
    'sdxl', 'sd15', 'sd3', 'flux1', 'flux2-klein-4b', 'flux2-klein-9b', 'flux2-dev', 'zimage', 'qwen-image', 'krea2',
    'anima', 'moss', 'ideogram4', 'wan',
];
export const FamilyIdSchema = z.enum(FAMILY_IDS);
/** "Works with any model": upscalers, detectors … */
export const ANY_FAMILY = 'any';
export const FamilyChoiceSchema = z.union([FamilyIdSchema, z.literal(ANY_FAMILY)]);
/** What the agent recognises a file as (structure) — base models use their family id. */
export const COMPONENT_ARCHS = [
    'vae-sd', 'ae', 'flux2-vae', 'wan-vae', 't5xxl', 'umt5xxl', 'clip-l', 'clip-g', 'qwen3-06b', 'qwen3-4b', 'qwen3-8b',
    'qwen3vl-4b', 'qwen3vl-8b', 'qwen25vl-7b', 'qwen35-9b', 'mistral3-24b',
];
export const ArchSchema = z.enum([...FAMILY_IDS, ...COMPONENT_ARCHS]);
const vae = (...anyOf) => ({ label: 'VAE', folder: 'vae', anyOf });
const te = (label, ...anyOf) => ({ label: `text encoder ${label}`, folder: 'text_encoders', anyOf });
export const FAMILIES = Object.freeze([
    { id: 'sdxl', label: 'SDXL (with Pony, Illustrious)', needs: [] },
    { id: 'sd15', label: 'Stable Diffusion 1.5', needs: [] },
    { id: 'sd3', label: 'Stable Diffusion 3.5', needs: [te('CLIP-L', 'clip-l'), te('CLIP-G', 'clip-g'), te('T5-XXL', 't5xxl')] },
    { id: 'flux1', label: 'FLUX.1', needs: [vae('ae'), te('CLIP-L', 'clip-l'), te('T5-XXL', 't5xxl')] },
    { id: 'flux2-klein-4b', label: 'FLUX.2 [klein] 4B', needs: [vae('flux2-vae'), te('Qwen3 4B', 'qwen3-4b')] },
    { id: 'flux2-klein-9b', label: 'FLUX.2 [klein] 9B', needs: [vae('flux2-vae'), te('Qwen3 8B', 'qwen3-8b')] },
    { id: 'flux2-dev', label: 'FLUX.2 [dev]', needs: [vae('flux2-vae'), te('Mistral 3 24B', 'mistral3-24b')] },
    { id: 'zimage', label: 'Z-Image', needs: [vae('ae'), te('Qwen3 4B', 'qwen3-4b')] },
    { id: 'qwen-image', label: 'Qwen-Image', needs: [vae('wan-vae'), te('Qwen2.5-VL 7B', 'qwen25vl-7b')] },
    { id: 'krea2', label: 'Krea 2', needs: [vae('wan-vae'), te('Qwen3-VL 4B', 'qwen3vl-4b')] },
    { id: 'anima', label: 'Anima', needs: [vae('wan-vae'), te('Qwen3 0.6B', 'qwen3-06b')] },
    { id: 'moss', label: 'MOSS-Image', needs: [vae('flux2-vae'), te('Qwen3.5 9B', 'qwen35-9b')] },
    { id: 'ideogram4', label: 'Ideogram 4', needs: [vae('flux2-vae'), te('Qwen3-VL 8B', 'qwen3vl-8b')] },
    { id: 'wan', label: 'Wan (video)', needs: [vae('wan-vae'), te('UMT5-XXL', 'umt5xxl')] },
]);
/** Shared parts and every family that uses them (from the knowledge base, doc 44). */
export const COMPONENT_FAMILIES = Object.freeze({
    'vae-sd': ['sdxl', 'sd15'],
    ae: ['flux1', 'zimage'],
    'flux2-vae': ['flux2-klein-4b', 'flux2-klein-9b', 'flux2-dev', 'moss', 'ideogram4'],
    'wan-vae': ['qwen-image', 'krea2', 'anima', 'wan'],
    t5xxl: ['flux1', 'sd3'],
    umt5xxl: ['wan'],
    'clip-l': ['flux1', 'sd3'],
    'clip-g': ['sd3'],
    'qwen3-06b': ['anima'],
    'qwen3-4b': ['zimage', 'flux2-klein-4b'],
    'qwen3-8b': ['flux2-klein-9b'],
    'qwen3vl-4b': ['krea2'],
    'qwen3vl-8b': ['ideogram4'],
    'qwen25vl-7b': ['qwen-image'],
    'qwen35-9b': ['moss'],
    'mistral3-24b': ['flux2-dev'],
});
/** The families a recognised file belongs to. */
export function familiesOf(arch) {
    return FAMILY_IDS.includes(arch) ? [arch] : COMPONENT_FAMILIES[arch];
}
/** "SDXL (with Pony, Illustrious)", or "any model" for upscalers and the like. */
export function familyLabel(id) {
    return id === ANY_FAMILY ? 'any model' : FAMILIES.find((f) => f.id === id)?.label ?? id;
}
export const FamilySourceSchema = z.enum(['manual', 'header', 'structure', 'name', 'folder']);
//# sourceMappingURL=families.js.map